import { useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetClientUser } from '~/hooks';
import { ROUTES, replaceRouteParams } from '~/utils/routes';
import { convertToFormValues } from '../../logics/convertToFormValues';
import { generatePutRequestBody } from '../../logics/generatePutRequestBody';
import { type EditUserFormValues, editUserFormSchema } from '../../logics/schema';
import { EditUserFormPresentation } from '../../presentations';
import { useGetClientBranches } from '../useGetClientBranches';
import { usePutClientUser } from '../usePutClientUser';

type Args = {
  userId: number;
};

type PresentationProps = React.ComponentProps<typeof EditUserFormPresentation>;

type IUseEditUserForm = (args: Args) => {
  state: {
    userData: ReturnType<typeof useGetClientUser>['data'];
    methods: ReturnType<typeof useForm<EditUserFormValues>>;
    branchOptions: PresentationProps['branchOptions'];
    isMutating: boolean;
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
    handleCancel: PresentationProps['onCancel'];
  };
};

/**
 * 利用者編集フォームの状態管理とアクションを提供する
 */
export const useEditUserForm: IUseEditUserForm = ({ userId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() || '';

  // 利用者情報の取得
  const { data: userData } = useGetClientUser({ threeLetterCode, id: userId });

  // 事業所一覧の取得
  const { data: branchesData } = useGetClientBranches({ threeLetterCode });
  const branchOptions = (branchesData.branches ?? []).map(branch => ({
    value: String(branch.id),
    label: `${branch.name}（${branch.code}）`,
  }));

  // react-hook-form の初期化
  const methods = useForm<EditUserFormValues>({
    defaultValues: convertToFormValues(userData),
    criteriaMode: 'all',
    mode: 'onBlur',
    resolver: zodResolver(editUserFormSchema),
  });

  // 利用者情報更新 API hook
  const { trigger, isMutating } = usePutClientUser(threeLetterCode, userId, {
    onSuccess: () => {
      showToast({ message: '利用者情報を更新しました。', status: 'success' });
      navigate(replaceRouteParams(ROUTES.USER_SHOW, { id: userId }));
    },
    onError: () => {
      showToast({ message: '利用者情報の更新に失敗しました。', status: 'danger' });
    },
  });

  /**
   * フォームを送信する
   */
  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formValues => {
    trigger(
      generatePutRequestBody({
        formValues,
      })
    );
  });

  /**
   * 利用者詳細画面へ戻る
   */
  const handleCancel: PresentationProps['onCancel'] = () => {
    navigate(replaceRouteParams(ROUTES.USER_SHOW, { id: userId }));
  };

  return {
    state: {
      userData,
      methods,
      branchOptions,
      isMutating,
    },
    actions: { handleSubmit, handleCancel },
  };
};
